/**
 * Over-ordering controls, client side.
 *
 * The cap is the client's own best single delivery over the lookback window
 * plus an allowance — their best day, plus a fifth. It is never a target and
 * never the suggested quantity: it is the line past which an order stops being
 * a judgement call and starts being a loss the bakery carries.
 *
 * Nothing here blocks an order. The warning is the product.
 */

// Mirrors CAP_REASONS in backend/services/order_cap_service.py. The server
// sends its own list with every insights read; this is the fallback so a
// checkout never loses its reason picker to one failed request.
export const FALLBACK_CAP_REASONS = {
  event: 'An event or promotion',
  new_site: 'A new site or outlet',
  seasonal: 'A seasonal push',
  other: 'Something else',
};

// {product_id: cap} from an order-insights payload.
export const buildCapMap = (insights) => {
  const map = {};
  (insights?.caps || []).forEach((c) => { map[c.product_id] = c; });
  return map;
};

export const reasonEntries = (reasons) =>
  Object.entries(reasons && Object.keys(reasons).length ? reasons : FALLBACK_CAP_REASONS);

/**
 * The cart lines sitting above their cap, each carrying the figures behind it.
 * `lines` is [{ product_id, product_name, quantity }] — the shape both the
 * client cart and the admin on-behalf form can produce.
 */
export const findOverCapLines = (lines, capMap) =>
  (lines || []).reduce((out, line) => {
    const cap = capMap?.[line.product_id];
    if (!cap || !cap.cap_qty) return out;
    const ordered = Number(line.quantity) || 0;
    if (ordered <= cap.cap_qty) return out;
    out.push({
      ...cap,
      product_name: line.product_name || cap.product_name,
      ordered_qty: ordered,
      over_by: ordered - cap.cap_qty,
    });
    return out;
  }, []);

// Cart items use `id`/`name`; normalise before comparing.
export const cartToLines = (cart) =>
  (cart || []).map((it) => ({
    product_id: it.id,
    product_name: it.name,
    quantity: it.quantity,
  }));

export const formatDay = (iso) => {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const formatShortDay = (iso) => {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
